'use client';

import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { motion } from 'framer-motion';
import { tv } from 'tailwind-variants';

import { cn } from '@/utils/cn-tw-merge';

interface TabsOffset {
  [ket: string]: {
    width: number;
    offset: number;
  };
}

const TabsContext = React.createContext<{
  tabs: TabsOffset;
  activeTab: string;
  setTabs: React.Dispatch<React.SetStateAction<TabsOffset>>;
  setActiveTab: (tab: string) => void;
} | null>(null);

export const useTabs = () => {
  const context = React.useContext(TabsContext);
  if (!context) {
    throw new Error('useTabs must be used within a TabsProvider');
  }
  return context;
};

const TabsProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeTab, setActiveTab] = React.useState<string>('');
  const [tabs, setTabs] = React.useState<TabsOffset>({});

  return <TabsContext.Provider value={{ activeTab, tabs, setActiveTab, setTabs }}>{children}</TabsContext.Provider>;
};

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(({ ...props }, ref) => {
  const { setActiveTab } = useTabs();

  React.useEffect(() => {
    setActiveTab(props.defaultValue || '');
  }, []);

  React.useEffect(() => {
    if (props.value) {
      setActiveTab(props.value || '');
    }
  }, [props?.value]);

  return (
    <TabsPrimitive.Root
      ref={ref}
      onValueChange={(value) => {
        props?.onValueChange?.(value);
        setActiveTab(value);
      }}
      {...props}
    />
  );
});

Tabs.displayName = TabsPrimitive.Root.displayName;

const tabsList = tv({
  base: 'inline-flex items-center justify-center rounded-lg border p-1 relative ',
  variants: {
    variant: {
      default: 'bg-gray-100 border-gray-300 text-gray-700 dark:bg-slate-800 dark:text-gray-700',
      green: 'bg-green-50 border-green-100 text-green-600 dark:bg-green-800 dark:text-green-400',
    },
  },
});

const tabItem = tv({
  base: 'absolute h-9 rounded-[5px] left-1 z-0',
  variants: {
    variant: {
      default: 'bg-gray-700',
      green: 'bg-green-400',
    },
  },
});

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & { variant?: 'default' | 'green' }
>(({ className, variant = 'default', ...props }, ref) => {
  const { tabs, activeTab } = useTabs();

  const currentTab = tabs[activeTab];

  return (
    <TabsPrimitive.List ref={ref} className={cn(tabsList({ variant }), className)} {...props}>
      <motion.div
        className={tabItem({ variant })}
        layoutId="tabBackground"
        initial={false}
        animate={{ x: currentTab?.offset }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        style={{ width: currentTab?.width }}
      />
      <div className="relative z-10 flex flex-row">{props.children}</div>
    </TabsPrimitive.List>
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const { setTabs } = useTabs();
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    const updateTabMetrics = () => {
      setTabs((prev) => ({
        ...prev,
        [props.value]: {
          width: triggerRef.current?.offsetWidth || 0,
          offset: triggerRef.current?.offsetLeft || 0,
        },
      }));
    };

    updateTabMetrics();

    window.addEventListener('resize', updateTabMetrics);
    return () => window.removeEventListener('resize', updateTabMetrics);
  }, [props.value, setTabs]);

  return (
    <TabsPrimitive.Trigger
      ref={ref || triggerRef}
      className={cn(
        'inline-flex h-9 items-center justify-center whitespace-nowrap px-4 rounded-[5px] first:pl-4 last:pr-4 data-[state=active]:px-4 data-[state=active]:py-2.5 text-lg data-[state=active]:font-semibold ring-offset-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-gray-700 data-[state=active]:text-white data-[state=active]:shadow-sm dark:text-gray-700 dark:data-[state=active]:text-slate-50 bg-transparent z-20',
        className,
      )}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'data-[state=inactive]:hidden ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 dark:ring-offset-slate-950 dark:focus-visible:ring-slate-300',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { TabsProvider, Tabs, TabsList, TabsTrigger, TabsContent };
